// Feature B — Biome Texture Overlay
// Adds a subtle per-biome grain/texture on top of base tiles.
// Uses Option 1 (per-tile at render time) for simplicity and compatibility.

import { hexCorners, hexKey, getNeighbor } from '../../math/hexMath.mjs';
import { hash01 } from './noise.mjs';
import { TEXTURE_ALPHA_BY_TYPE, REGION_TINT_STRENGTH } from './constants.mjs';

// Number of texture pixels per biome (fewer = less tile coverage)
const DOT_COUNTS = {
  grass:       8,
  grassland:   7,
  desert:     28,  // more dots for visible shimmer
  snow:        6,
  mountain:    8,
  deadplains:  9,
  ocean:       0,
};

// Accent colour per biome
const DOT_COLORS = {
  grass:      '#3a6018',
  grassland:  '#5a8030',
  desert:     '#ffe060',  // bright gold for visible shimmer
  snow:       '#c8dce8',
  mountain:   '#5a4020',
  deadplains: '#4a3e34',
  ocean:      '#1a6a9a',
};

// Desert dot animation: bucket period in ms (how fast dots wink)
const DESERT_SHIMMER_MS = 220;

/** Count neighbours whose majority type matches `biome`. */
function countSameBiomeNeighbours(tile, biome, tiles) {
  if (!tiles) return 0;
  const { q, r } = tile.coord;
  let count = 0;
  for (let s = 0; s < 6; s++) {
    const nb = getNeighbor(q, r, s);
    const nbTile = tiles.get(hexKey(nb.q, nb.r));
    if (nbTile && nbTile.placed && getMajorityType(nbTile.sides) === biome) {
      if (++count >= 3) return count; // early exit once threshold met
    }
  }
  return count;
}

/** Returns the majority tile type from the sides array. */
function getMajorityType(sides) {
  const counts = {};
  for (const s of sides) counts[s] = (counts[s] || 0) + 1;
  let best = sides[0];
  let bestCount = 0;
  for (const [type, count] of Object.entries(counts)) {
    if (count > bestCount) { bestCount = count; best = type; }
  }
  return best;
}

export class BiomeTextureFx {
  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param {Array<{tile: object, sp: {x:number, y:number}}>} visibleTiles
   * @param {number} worldSeed
   * @param {number} sz   – hex radius in screen pixels
   * @param {number} [nowMs] – current timestamp for animated biomes
   * @param {Map<string,object>} [tiles] – full tile map for neighbour checks
   */
  render(ctx, visibleTiles, worldSeed, sz, nowMs = 0, tiles = null) {
    const desertBucket = Math.floor(nowMs / DESERT_SHIMMER_MS);

    for (const { tile, sp } of visibleTiles) {
      const majority = getMajorityType(tile.sides);
      const alpha = TEXTURE_ALPHA_BY_TYPE[majority] ?? 0;
      if (alpha <= 0) continue;

      const count = DOT_COUNTS[majority] ?? 8;
      const { q, r } = tile.coord;
      const isDesert = majority === 'desert';

      // Desert shimmer only on tiles with ≥3 adjacent desert tiles
      if (isDesert && countSameBiomeNeighbours(tile, 'desert', tiles) < 3) continue;

      ctx.save();

      // Clip to hex so dots don't bleed onto neighbours
      const corners = hexCorners(sp.x, sp.y, sz * 0.92);
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i].x, corners[i].y);
      ctx.closePath();
      ctx.clip();

      ctx.fillStyle = DOT_COLORS[majority] ?? '#888';

      // Pixel size: 1 at normal zoom, 2 when zoomed in (sz > 55)
      const ps = sz > 55 ? 2 : 1;

      // Draw deterministic crisp pixels distributed inside the hex
      for (let j = 0; j < count; j++) {
        const a = hash01(q * 13 + j, r * 7,      worldSeed + j);
        const b = hash01(q * 11,     r * 9 + j,  worldSeed + j * 17);

        // Polar distribution with sqrt for uniform area coverage
        const angle  = a * Math.PI * 2;
        const radius = Math.sqrt(b) * sz * 0.72;

        // Snap to integer pixel grid for a crisp, pixelated look
        const px = Math.round(sp.x + Math.cos(angle) * radius);
        const py = Math.round(sp.y + Math.sin(angle) * radius);

        if (isDesert) {
          // Desert dots animate: each dot has its own flicker phase
          const phase = hash01(q * 5 + j * 7, r * 11 + j * 3, desertBucket);
          if (phase > 0.55) {
            // Dot is "off" this frame — draw dim base speck
            ctx.globalAlpha = 0.15;
            ctx.fillStyle = '#c08820';
            ctx.fillRect(px, py, ps, ps);
            ctx.fillStyle = DOT_COLORS[majority];
            continue;
          }
          // Dot is "on": bright gold shimmer
          const bright = phase < 0.2;
          ctx.globalAlpha = bright ? 0.85 : 0.5 + (0.55 - phase) * 0.7;
          ctx.fillStyle   = bright ? '#fff8a0' : '#ffe060';
        } else {
          ctx.globalAlpha = alpha * (0.5 + a * 0.5);
        }

        ctx.fillRect(px, py, ps, ps);
      }

      // Regional variance: a very low-frequency tint to break up large areas
      const regionNoise = hash01(
        Math.floor(q * 0.15 + 0.5) | 0,
        Math.floor(r * 0.15 + 0.5) | 0,
        (worldSeed * 13) | 0,
      );
      const tintAlpha = REGION_TINT_STRENGTH * 0.5;
      ctx.globalAlpha = tintAlpha;
      ctx.fillStyle = regionNoise > 0.5 ? '#ffffff' : '#000000';
      const c2 = hexCorners(sp.x, sp.y, sz * 0.92);
      ctx.beginPath();
      ctx.moveTo(c2[0].x, c2[0].y);
      for (let i = 1; i < c2.length; i++) ctx.lineTo(c2[i].x, c2[i].y);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    }
  }
}
